import {CloseButton} from "../buttons/CloseButton";
import {formatToRandWithDecimalsString} from "../../core/helpers/textFormatting";
import React, {useContext} from "react";
import {GreenBoxNumber} from "../views/GreenBoxNumber";
import {ClipLoader} from "react-spinners";
import {HalfwayContext} from "../../context/HalfwayContext";
import {HalfwayContextType} from "../../types/HalfwayContextType";
import {IMenuItem} from "../../core/interfaces/IMenuItem";
import {getBasketOptionIssue} from "../../core/helpers/menuItemOptions";
import {getMenuItemLinePrice} from "../../core/helpers/menuItemPricing";

export const BasketDrawer = () => {

    const {
        toggleBasketDrawer,
        setShowBasketDrawer,
        toggleProductDrawer,
        addToBasketFunction,
        menuItems,
        totalItemsInBasket,
        totalCostOfItemsInBasket,
        savingOrder,
        orderSaveError,
        golferFields,
        golferFieldValues,
        setGolferFieldValue,
        postOrderToFirebase
    } = useContext(HalfwayContext) as HalfwayContextType;

    const getBasketItems = (): Array<IMenuItem> =>
    {
        return menuItems.filter(x => (x?.totalInBasket ?? 0) > 0);
    }

    const basketOptionIssue = getBasketItems().map(getBasketOptionIssue).find(Boolean);

    return (
        <div className='flex flex-col h-full min-h-0 overflow-y-auto overscroll-contain bg-white'>

            {/* Drawer Handle */}
            <div className="drawer-handle"></div>

            <div
                className='flex flex-col px-5'
                style={{paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))'}}>

                {/* Header */}
                <div className='flex justify-between items-center mt-2'>
                    <div>
                        <h2 className='text-xl font-bold text-slate-900 tracking-tight'>Your Order</h2>
                        <span className='text-sm text-slate-400 mt-0.5 block'>
                            {totalItemsInBasket} {totalItemsInBasket === 1 ? 'item' : 'items'}
                        </span>
                    </div>
                    <CloseButton closeFunction={toggleBasketDrawer}/>
                </div>

                {/* Divider */}
                <div className='h-px bg-slate-100 mt-4 mb-3'></div>

                {/* Basket Items */}
                <div className='flex flex-col gap-3'>
                    {getBasketItems().map(basketItem => {
                        return (
                            <div key={basketItem.id}
                                 className='bg-slate-50 rounded-xl p-3'>
                                <div className='flex items-start gap-3'>
                                <div className='flex-shrink-0'>
                                    <GreenBoxNumber value={basketItem?.totalInBasket} />
                                </div>
                                <div className='flex-1 min-w-0'>
                                    <span className='text-sm font-medium text-slate-800 block break-words'>
                                        {basketItem?.name}
                                    </span>
                                </div>
                                <span className='text-sm font-semibold text-slate-700 flex-shrink-0'>
                                    {formatToRandWithDecimalsString(getMenuItemLinePrice(basketItem))}
                                </span>
                                </div>
                                {getBasketOptionIssue(basketItem) && (
                                    <div className='mt-3 text-sm'>
                                        <p className='text-red-700' role='alert'>{getBasketOptionIssue(basketItem)}</p>
                                        <div className='flex gap-4 mt-2'>
                                            <button type='button' className='font-semibold text-maroon' disabled={savingOrder}
                                                onClick={() => { setShowBasketDrawer(false); toggleProductDrawer(basketItem, true); }}>
                                                Review item
                                            </button>
                                            <button type='button' className='font-semibold text-maroon' disabled={savingOrder}
                                                onClick={() => addToBasketFunction({...basketItem, totalInBasket: 0})}>
                                                Remove item
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {(basketItem.selectedOptions ?? []).length > 0 && (
                                    <div className='mt-2 text-xs text-slate-500'>
                                        <dl className='space-y-1.5'>
                                            {basketItem.selectedOptions?.map((option, index) => (
                                                <div key={`${option.optionSetId}-${option.optionId}-${index}`} className='flex justify-between gap-3'>
                                                    <dt className='min-w-0 break-words'>
                                                        {option.optionName ?? 'Selected option'}
                                                    </dt>
                                                    <dd className='flex-shrink-0'>
                                                        {formatToRandWithDecimalsString((option.additionalPrice ?? 0) * basketItem.totalInBasket)}
                                                    </dd>
                                                </div>
                                            ))}
                                        </dl>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>

                {/* Divider */}
                <div className='h-px bg-slate-100 mt-4 mb-3'></div>

                {/* Subtotal */}
                <div className='flex justify-between items-center mb-1'>
                    <span className='text-base font-bold text-slate-900'>Subtotal</span>
                    <span className='text-base font-bold text-slate-900'>
                        {formatToRandWithDecimalsString(totalCostOfItemsInBasket)}
                    </span>
                </div>

                <span className='text-xs text-slate-400 mb-5'>
                    Payment can be made once you reach the halfway house.
                </span>

                {golferFields.map(field => (
                    <div className='mb-4' key={field.id}>
                        <label htmlFor={`golfer-${field.id}`} className="field-label">
                            {field.label} {field.required
                                ? <span className='text-maroon'>*</span>
                                : <span className='text-slate-400 font-normal'>(optional)</span>}
                        </label>
                        <input id={`golfer-${field.id}`}
                               type="text"
                               className="form-input"
                               maxLength={256}
                               required={field.required}
                               aria-required={field.required}
                               disabled={savingOrder}
                               autoComplete={field.id === 'name' ? 'name' : 'off'}
                               onChange={e => setGolferFieldValue(field.id, e.target.value)}
                               value={golferFieldValues[field.id] ?? ''} />
                    </div>
                ))}

                {orderSaveError &&
                    <div className='text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3 mb-4 border border-red-100' role='alert'>
                        {orderSaveError}
                    </div>
                }

                {/*Place Order Button*/}
                <button className="w-full disabled:opacity-50 disabled:cursor-not-allowed"
                        type='button'
                        disabled={savingOrder || !!basketOptionIssue}
                        onClick={() => postOrderToFirebase()}>
                    <div className='button'>
                        {savingOrder
                            ?   <div className='flex items-center justify-center gap-2'>
                                    <ClipLoader color="#fff" size={18}/>
                                    <span>Placing your order…</span>
                                </div>
                            : <span>Confirm & Place Order</span>
                        }
                    </div>
                </button>

                {/* Bottom safe area spacer */}
                <div className='h-8'></div>
            </div>
        </div>
    )
}
